import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  Save, 
  Plus, 
  Trash2, 
  Truck, 
  Calendar, 
  FileText,
  Info,
  Settings2,
  AlertCircle
} from 'lucide-react';
import { useEquipmentRequestsNew } from './hooks/useEquipmentRequestsNew';
import { cn } from '@/utils/cn';

export function EquipmentRequestNewPage() {
  const navigate = useNavigate();
  const { 
    formData, 
    projects, 
    categories, 
    isLoading, 
    isSaving, 
    setFormData, 
    addItem, 
    removeItem, 
    createRequest 
  } = useEquipmentRequestsNew();

  const [newItem, setNewItem] = useState({ equipmentTypeId: '', quantity: 1, note: '' });

  const handleAddItem = () => {
    const cat = categories.find(c => c.id === newItem.equipmentTypeId);
    if (!cat) return;
    addItem({ ...newItem, categoryName: cat.name });
    setNewItem({ equipmentTypeId: '', quantity: 1, note: '' });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 pb-12">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/equipment/requests')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-normal text-slate-900 tracking-tight">Yêu cầu thiết bị</h1>
            <p className="sm:text-sm text-xs text-slate-500">Gửi đề xuất điều động thiết bị cho dự án thi công.</p>
          </div>
        </div>
        <button
          onClick={createRequest}
          disabled={isSaving || !formData.title || formData.items.length === 0}
          className="flex h-10 items-center gap-2 rounded-md bg-slate-900 px-4 sm:text-sm text-xs font-normal text-white -slate-900/10 hover:bg-slate-800 disabled:opacity-50 transition-all active:scale-95"
        >
          <Save className="h-4 w-4" />
          <span>{isSaving ? 'Đang gửi...' : 'Gửi yêu cầu'}</span>
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-12">
        {/* Main Form */}
        <div className="md:col-span-8 space-y-4">
          {/* Base Info */}
          <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 font-normal text-slate-800">
              <FileText className="h-4 w-4 text-slate-900" />
              <span>Nội dung đề xuất</span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-1.5">
                <label className="sm:text-sm text-xs font-normal text-slate-500 tracking-wider">Mục đích điều động</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Đào móng khu dân cư A..."
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 sm:text-sm text-xs outline-none focus:border-slate-900 focus:bg-white transition-all"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="sm:text-sm text-xs font-normal text-slate-500 tracking-wider">Dự án tiếp nhận</label>
                <select
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs outline-none focus:border-slate-900 transition-all"
                  value={formData.projectId}
                  onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                >
                  <option value="">Chọn dự án...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="sm:text-sm text-xs font-normal text-slate-500 tracking-wider">Ngày bắt đầu cần</label>
                <input
                  type="date"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs outline-none focus:border-slate-900 transition-all"
                  value={formData.neededFrom}
                  onChange={(e) => setFormData({ ...formData, neededFrom: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <label className="sm:text-sm text-xs font-normal text-slate-500 tracking-wider">Mô tả chi tiết nhu cầu</label>
                <textarea
                  rows={3}
                  placeholder="Nhập ghi chú thêm về cấu hình thiết bị nếu cần..."
                  className="w-full rounded-md border border-slate-200 bg-slate-50/50 p-3 sm:text-sm text-xs outline-none focus:border-slate-900 focus:bg-white transition-all"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>
          </section>

          {/* Items Section */}
          <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
             <div className="flex items-center justify-between border-b border-slate-50 pb-2">
              <div className="flex items-center gap-2 font-normal text-slate-800">
                <Settings2 className="h-4 w-4 text-slate-900" />
                <span>Thiết bị cần điều động</span>
              </div>
              <span className="text-[10px] font-normal text-slate-400 tracking-widest">{formData.items.length} loại máy</span>
            </div>

            {/* Quick Add */}
            <div className="grid gap-3 rounded-md bg-slate-50 p-3 sm:grid-cols-12">
              <div className="sm:col-span-6 space-y-1">
                <label className="text-[10px] font-normal text-slate-400 tracking-wider">Loại thiết bị</label>
                <select
                  className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 sm:text-sm text-xs outline-none focus:border-slate-900"
                  value={newItem.equipmentTypeId}
                  onChange={(e) => setNewItem({ ...newItem, equipmentTypeId: e.target.value })}
                >
                  <option value="">Chọn loại...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2 space-y-1">
                <label className="text-[10px] font-normal text-slate-400 tracking-wider">Số lượng</label>
                <input
                  type="number"
                  className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 sm:text-sm text-xs outline-none focus:border-slate-900"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: Number(e.target.value) })}
                />
              </div>
              <div className="sm:col-span-4 flex items-end">
                <button
                  type="button"
                  onClick={handleAddItem}
                  disabled={!newItem.equipmentTypeId}
                  className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-white border border-slate-200 sm:text-sm text-xs font-normal text-slate-700 hover:bg-slate-100 transition-all disabled:opacity-50"
                >
                  <Plus className="h-3 w-3" />
                  Thêm vào list
                </button>
              </div>
            </div>

            {/* Items Table */}
            <div className="overflow-x-auto">
               <table className="w-full text-left sm:text-sm text-xs">
                <thead>
                  <tr className="border-b border-slate-50 text-slate-400 font-normal tracking-tighter">
                    <th className="pb-2 font-normal">Loại thiết bị</th>
                    <th className="pb-2 text-right font-normal">Số lượng</th>
                    <th className="pb-2 text-right font-normal">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {formData.items.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-slate-400 italic font-normal">Chưa có thiết bị nào được chọn</td>
                    </tr>
                  ) : (
                    formData.items.map((item) => (
                      <tr key={item.id} className="group">
                        <td className="py-3">
                          <p className="font-normal text-slate-700">{item.categoryName}</p>
                        </td>
                        <td className="py-3 text-right font-normal text-slate-900">
                          {item.quantity} chiếc
                        </td>
                        <td className="py-3 text-right">
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="h-7 w-7 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors inline-flex items-center justify-center"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <div className="md:col-span-4 space-y-4">
           <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
            <h3 className="text-[10px] font-normal text-slate-400 tracking-widest border-b border-slate-50 pb-2">Ưu tiên & Thời gian</h3>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="sm:text-sm text-xs font-normal text-slate-500 tracking-wider">Độ khẩn cấp</label>
                <div className="grid grid-cols-2 gap-2">
                   {(['normal', 'high', 'urgent'] as const).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFormData({ ...formData, priority: p })}
                      className={cn(
                        'rounded-md border p-2 text-[10px] font-normal  tracking-widest transition-all',
                        formData.priority === p
                          ? 'border-slate-900 bg-slate-900 text-white '
                          : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                      )}
                    >
                      {p === 'normal' ? 'Bình thường' : p === 'high' ? 'Cao' : 'Khẩn'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="sm:text-sm text-xs font-normal text-slate-500 tracking-wider">Ngày kết thúc dự kiến</label>
                <input
                  type="date"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs outline-none focus:border-slate-900 transition-all"
                  value={formData.neededUntil}
                  onChange={(e) => setFormData({ ...formData, neededUntil: e.target.value })}
                />
                <p className="text-[10px] font-normal text-slate-400 italic">Để trống nếu không xác định thời gian trả máy.</p>
              </div>
            </div>
           </section>

           <div className="rounded-md bg-slate-50 border border-slate-100 p-4 space-y-3">
             <div className="flex gap-2 text-slate-800">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <p className="text-[10px] font-normal tracking-tighter">Quy trình điều phối</p>
             </div>
             <p className="text-[10px] text-slate-500 leading-relaxed font-normal">
               Yêu cầu sau khi gửi sẽ được **Trưởng phòng Thiết bị** phê duyệt và chỉ định mã máy cụ thể trước khi bàn giao cho dự án.
             </p>
           </div>
        </div>
      </div>
    </div>
  );
}
